import React, { FC, useState } from "react"

import {
    CheckCircleFilled,
    CloseCircleFilled,
    InfoCircleOutlined,
    PlayCircleOutlined,
    SaveOutlined,
    PhoneOutlined,
    CalendarOutlined,
    CrownOutlined,
    FileTextOutlined
} from "@ant-design/icons"
import { Alert, Button, Collapse, Input, Card, Checkbox } from "antd"

import { useTypedSelector, useAppDispatch } from "../../../hooks/useTypedSelector"
import { currentUserActions } from "../../../../redux/slices/currentUser/actions"
import { currentUserSliceActions } from "../../../../redux/slices/currentUser/currentUserSlice"
import { mainApi } from "../../../api/endpoints/mainApi"
import { showErrorNotification, showSuccessNotification } from "../../../utils/ui"
import { ICianSubscription } from "../../../../redux/slices/currentUser/types"

import "./CianSettingsPage.scss"

const { TextArea } = Input

// Константы источников автозвонка
const AUTOCALL_SOURCE_AVITO = 1
const AUTOCALL_SOURCE_YANDEX = 2
const AUTOCALL_SOURCE_CIAN = 3

const CianSettingsPage: FC = () => {
    const [cookies, setCookies] = useState("")
    const [isSaving, setIsSaving] = useState(false)
    const [isSavingSources, setIsSavingSources] = useState(false)

    const dispatch = useAppDispatch()

    // Берём статус авторизации, подписку и источники из Redux
    const { cian_auth_status, cian_subscription, autocall_sources } = useTypedSelector((state) => state.currentUser)

    // Обработчик изменения источников автозвонка
    const handleSourceChange = async (sourceId: number, checked: boolean) => {
        const newSources = checked
            ? [...autocall_sources, sourceId]
            : autocall_sources.filter(s => s !== sourceId)

        setIsSavingSources(true)
        try {
            const response = await mainApi.saveAutocallSources(newSources)
            if (response.status === 200 && response.data.success) {
                dispatch(currentUserSliceActions.currentUserSetAutocallSources(newSources))
                showSuccessNotification("Источники автозвонка сохранены")
            }
        } catch {
            showErrorNotification("Ошибка при сохранении источников")
        } finally {
            setIsSavingSources(false)
        }
    }

    const saveCookies = async () => {
        if (!cookies.trim()) {
            showErrorNotification("Введите куки")
            return
        }

        setIsSaving(true)
        try {
            const response = await mainApi.saveCianCookies(cookies)
            if (response.status === 200 && response.data.success) {
                showSuccessNotification("Куки успешно сохранены и проверены")
                setCookies("") // Очищаем поле после успешного сохранения

                // Сохраняем subscription из ответа напрямую в Redux
                if (response.data.subscription) {
                    dispatch(currentUserSliceActions.currentUserSetCianSubscription(response.data.subscription as ICianSubscription))
                }

                // Обновляем статус авторизации
                dispatch(currentUserActions.currentUserGetStatus())
            }
        } catch (error: unknown) {
            // Обрабатываем ошибку от сервера (статус 400)
            if (error && typeof error === "object" && "response" in error) {
                const axiosError = error as { response?: { data?: { message?: string } } }
                const message = axiosError.response?.data?.message || "Куки недействительны или истекли"
                showErrorNotification(message)
            } else {
                showErrorNotification("Ошибка при сохранении куки")
            }
        } finally {
            setIsSaving(false)
        }
    }

    // Ссылка на видео-инструкцию
    const videoUrl = "https://disk.yandex.ru/i/qX6T_DE3mToHpw"

    return (
        <div className="cian-settings-page">
            <div className="cian-settings-header">
                <h2>Настройки авторизации ЦИАН</h2>
                <div className={`auth-status ${cian_auth_status === 1 ? 'authorized' : 'not-authorized'}`}>
                    {cian_auth_status === 1 ? (
                        <>
                            <CheckCircleFilled />
                            <span>Авторизован</span>
                        </>
                    ) : (
                        <>
                            <CloseCircleFilled />
                            <span>Не авторизован</span>
                        </>
                    )}
                </div>
            </div>

            {/* Блок выбора источников автозвонка */}
            <Card className="autocall-sources-card" title="Источники автозвонка">
                <div className="autocall-sources">
                    <Checkbox
                        checked={autocall_sources.includes(AUTOCALL_SOURCE_AVITO)}
                        onChange={(e) => handleSourceChange(AUTOCALL_SOURCE_AVITO, e.target.checked)}
                        disabled={isSavingSources}
                    >
                        <span className="source-label avito">Авито</span>
                    </Checkbox>
                    <Checkbox
                        checked={autocall_sources.includes(AUTOCALL_SOURCE_YANDEX)}
                        onChange={(e) => handleSourceChange(AUTOCALL_SOURCE_YANDEX, e.target.checked)}
                        disabled={isSavingSources}
                    >
                        <span className="source-label yandex">Яндекс Недвижимость</span>
                    </Checkbox>
                    <Checkbox
                        checked={autocall_sources.includes(AUTOCALL_SOURCE_CIAN)}
                        onChange={(e) => handleSourceChange(AUTOCALL_SOURCE_CIAN, e.target.checked)}
                        disabled={isSavingSources}
                    >
                        <span className="source-label cian">ЦИАН</span>
                    </Checkbox>
                </div>
                <div className="autocall-hint">
                    Выберите источники, с которых будут поступать звонки в автозвонок
                </div>
            </Card>

            <div className="cian-settings-content">
                {/* Карточка с информацией о подписке */}
                {cian_auth_status === 1 && cian_subscription && (
                    <Card className="subscription-card" title="Информация о подписке ЦИАН">
                        <div className="subscription-info">
                            <div className="subscription-item">
                                <CrownOutlined className="subscription-icon" />
                                <div className="subscription-details">
                                    <span className="subscription-label">Тариф</span>
                                    <span className="subscription-value">{cian_subscription.tariff}</span>
                                </div>
                            </div>

                            <div className="subscription-item">
                                <CalendarOutlined className="subscription-icon" />
                                <div className="subscription-details">
                                    <span className="subscription-label">Действует</span>
                                    <span className="subscription-value">{cian_subscription.expire_text}</span>
                                </div>
                            </div>

                            <div className="subscription-item">
                                <FileTextOutlined className="subscription-icon" />
                                <div className="subscription-details">
                                    <span className="subscription-label">Лимит</span>
                                    <span className="subscription-value">{cian_subscription.limit_info}</span>
                                </div>
                            </div>

                            <div className="subscription-item">
                                <PhoneOutlined className="subscription-icon" />
                                <div className="subscription-details">
                                    <span className="subscription-label">Телефон</span>
                                    <span className="subscription-value">{cian_subscription.phone}</span>
                                </div>
                            </div>
                        </div>

                        <div className={`subscription-status ${cian_subscription.status === 'active' ? 'active' : 'inactive'}`}>
                            {cian_subscription.status === 'active' ? 'Подписка активна' : 'Подписка неактивна'}
                        </div>
                    </Card>
                )}

                <Alert
                    message="Зачем нужны куки ЦИАН?"
                    description="Куки необходимы для выкупа номеров на ЦИАН. Без них вы не сможете дозвониться по номеру."
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    className="info-alert"
                />

                <Collapse className="instruction-collapse">
                    <Collapse.Panel header="Инструкция по получению куки" key="1">
                        <div className="video-instruction">
                            {videoUrl ? (
                                <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="video-link">
                                    <PlayCircleOutlined />
                                    <span>Смотреть видео-инструкцию</span>
                                </a>
                            ) : (
                                <div className="video-placeholder">
                                    <PlayCircleOutlined />
                                    <span>Видео-инструкция будет добавлена позже</span>
                                </div>
                            )}
                        </div>
                    </Collapse.Panel>
                </Collapse>

                <div className="cookies-form">
                    <label className="form-label">Куки ЦИАН:</label>
                    <TextArea
                        value={cookies}
                        onChange={(e) => setCookies(e.target.value)}
                        placeholder="Вставьте куки в формате: name=value; name2=value2; ..."
                        rows={6}
                        className="cookies-input"
                    />

                    <div className="form-actions">
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={saveCookies}
                            loading={isSaving}
                            size="large"
                        >
                            Сохранить куки
                        </Button>
                    </div>
                </div>

                <Alert
                    message="Важно!"
                    description="Куки имеют срок действия. Если авторизация перестала работать, повторите процедуру получения куки."
                    type="warning"
                    showIcon
                    className="warning-alert"
                />
            </div>
        </div>
    )
}

export default CianSettingsPage
